  (function () {
    const root = document.getElementById('partfinder-results');
    if (!root) return;

    const MIDDLEWARE_URL = root.dataset.middlewareUrl || '';
    const SHOP_ID = root.dataset.shopId || '';
    const STOREFRONT_TOKEN = root.dataset.storefrontToken || '';
    const STOREFRONT_DOMAIN = root.dataset.storefrontDomain || window.location.hostname;
    const STOREFRONT_API_URL = `https://${STOREFRONT_DOMAIN}/api/2026-04/graphql.json`;

    let allProducts = [];
    let activeCategory = null;

    const params = new URLSearchParams(window.location.search);
    const vehicleCode = params.get('code');

    // Elements live outside #partfinder-results in the page layout
    const countEl = document.querySelector('.pf-results-count');
    const filterInput = document.querySelector('.pf-filter-input');
    const categoryList = document.querySelector('.pf-category-list');
    const loadingEl = document.querySelector('.pf-loading');
    const errorEl = document.querySelector('.pf-results-error');
    const bikeHeaderEl = document.getElementById('pf-bike-header');
    const bikeNameEl = bikeHeaderEl?.querySelector('.pf-bike-name');
    const bikeDetailsEl = bikeHeaderEl?.querySelector('.pf-bike-details');
    const filterToggleBtn = document.getElementById('pf-filter-toggle');
    const sidebarEl = document.getElementById('pf-sidebar');

    // ── API ────────────────────────────────────────────────────────────────────

    async function fetchResults(code) {
      const res = await fetch(`${MIDDLEWARE_URL}/api/partfinder/results/${encodeURIComponent(SHOP_ID)}/${encodeURIComponent(code)}`);
      if (!res.ok) throw new Error(`Results lookup failed: ${res.status}`);
      const data = await res.json();
      return {
        products: Array.isArray(data.products) ? data.products : [],
        bike: data.bike || null,
      };
    }

    async function fetchProductsByResults(items) {
      if (!items.length) return [];

      // The middleware already resolved the vehicle code to concrete Shopify product IDs
      // (and the matching SKU per product), so we fetch those products directly by id
      // instead of guessing via a Storefront SKU text search.
      const skuById = new Map(items.map((item) => [`gid://shopify/Product/${item.id}`, String(item.sku)]));
      const gids = [...skuById.keys()];

      // Storefront API's `nodes` query caps input arrays at 250 ids per request.
      const NODES_CHUNK_SIZE = 250;
      const chunks = [];
      for (let i = 0; i < gids.length; i += NODES_CHUNK_SIZE) {
        chunks.push(gids.slice(i, i + NODES_CHUNK_SIZE));
      }

      const fetchChunk = async (chunkGids) => {
        const query1 = `
          {
            nodes(ids: ${JSON.stringify(chunkGids)}) {
              id
              ... on Product {
                title
                handle
                productType
                tags
                featuredImage { url altText }
                variants(first: 10) {
                  edges {
                    node { id title sku price { amount currencyCode } availableForSale }
                  }
                }
              }
            }
          }
        `;

        const query = `
          {
            nodes(ids: ${JSON.stringify(chunkGids)}) {
              id
              ... on Product {
                title
                handle
                productType
                tags
                featuredImage {
                  url
                  altText
                }

                availableForSale
                totalInventory

                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      sku

                      price {
                        amount
                        currencyCode
                      }

                      availableForSale
                      quantityAvailable
                      currentlyNotInStock

                      quantityRule {
                        minimum
                        maximum
                        increment
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        const res = await fetch(STOREFRONT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
          },
          body: JSON.stringify({ query }),
        });

        if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
        const json = await res.json();
        if (json.errors) throw new Error(`Storefront API error: ${json.errors[0]?.message || 'unknown'}`);
        return json.data.nodes;
      };

      const chunkResults = await Promise.all(chunks.map(fetchChunk));
      const nodes = chunkResults.flat();

      const products = [];
      for (const p of nodes) {
        if (!p || !p.variants) continue; // product deleted/unpublished since middleware indexed it
        const variants = p.variants.edges.map((e) => e.node);
        const wantedSku = skuById.get(p.id);
        const matched = variants.find((v) => v.sku === wantedSku) || variants[0];
        if (!matched) continue;
        products.push({
          id: p.id,
          title: p.title, 
          handle: p.handle,
          productType: p.productType,
          tags: p.tags,
          image: p.featuredImage,
          price: matched.price,
          variants,
          matchedSku: matched.sku,
          availableForSale: matched.availableForSale,
          currentlyNotInStock: matched.currentlyNotInStock,
          quantityAvailable: matched.quantityAvailable,
          quantityRule: matched.quantityRule,
          //maxOrderQuantity
        });
      }
      return products;
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    function formatPrice(price) {
      const amount = parseFloat(price.amount).toFixed(2).replace('.', ',');
      return `${amount} ${price.currencyCode === 'EUR' ? '€' : price.currencyCode}`;
    }

    function buttonForProduct(product) {
      const hasNoCart = product.tags.includes('no-cart');
      const url = `/products/${product.handle}`;

      if (hasNoCart) {
        return `<a class="pf-btn pf-btn--secondary" href="${url}">Weiterlesen</a>`;
      }
      if (product.variants.length > 1) {
        return `<a class="pf-btn pf-btn--primary" href="${url}">Ausführung wählen</a>`;
      }
      const variantId = product.variants[0]?.id || '';
      return `<button class="pf-btn pf-btn--primary pf-add-to-cart" data-variant-id="${variantId}">In den Warenkorb</button>`;
    }

    function renderCard(product) {
      const imgHtml = product.image
        ? `<img class="pf-card-img" src="${product.image.url}" alt="${product.image.altText || product.title}" loading="lazy">`
        // TSB-Platzhalter "Bild folgt" (Ticket 4246), dieselbe Datei wie in snippets/tsb-placeholder-image.liquid
        : `<img class="pf-card-img" src="https://cdn.shopify.com/s/files/1/0914/4398/6816/files/tsb-bild-folgt.png?v=1786197577&width=400" alt="Produktbild folgt" width="400" height="400" loading="lazy">`;

      const isAvailable =
        product.availableForSale &&
        !product.currentlyNotInStock;

      const actionsHtml = isAvailable
        ? `<div class="pf-card-actions">${buttonForProduct(product)}</div>`
        : `
          <div class="pf-card-actions">
            <button class="pf-btn pf-btn--primary pf-add-to-cart" disabled>
              Nicht verfügbar
            </button>
          </div>
        `;

      return `
        <div class="pf-card" data-type="${product.productType}" data-title="${product.title.toLowerCase()}">
          <a href="/products/${product.handle}" class="pf-card-img-wrap">${imgHtml}</a>

          <div class="pf-card-body">
            <a class="pf-card-title" href="/products/${product.handle}">
              ${product.title}
            </a>

            <p class="pf-card-price">
              ${formatPrice(product.price)} <span>inkl. MwSt.</span>
            </p>

            ${actionsHtml}
          </div>
        </div>
      `;
    }

    function renderGrid(products) {
      if (!products.length) {
        root.innerHTML = '<p class="pf-no-results">Keine Produkte gefunden.</p>';
        if (countEl) countEl.textContent = '0 Artikel gefunden';
        return;
      }
      root.innerHTML = `<div class="pf-results-grid">${products.map(renderCard).join('')}</div>`;
      if (countEl) countEl.textContent = `${products.length} ${products.length === 1 ? 'Artikel' : 'Artikel'} gefunden`;

      root.querySelectorAll('.pf-add-to-cart').forEach((btn) => {
        btn.addEventListener('click', () => addToCart(btn.dataset.variantId, btn));
      });
    }

    function renderBikeHeader(bike) {
      if (!bikeHeaderEl || !bike) return;

      const name = [bike.manufacturer_name, bike.version_name || bike.commercial_model_name]
        .filter(Boolean)
        .join(' ');
      const details = [
        bike.vehicle_year ? `Baujahr ${bike.vehicle_year}` : null,
        bike.category_name,
        bike.universe_name,
        bike.displacement_in_cm3 ? `${bike.displacement_in_cm3} cm³` : null,
      ].filter(Boolean);

      if (bikeNameEl) bikeNameEl.textContent = name;
      if (bikeDetailsEl) bikeDetailsEl.textContent = details.join(' · ');
      bikeHeaderEl.hidden = false;
    }

    function renderCategories(products) {
      if (!categoryList) return;
      const types = [...new Set(products.map((p) => p.productType).filter(Boolean))].sort();
      if (!types.length) { categoryList.hidden = true; return; }

      const allLi = `<li><a class="pf-cat-link pf-cat-link--active" href="#" data-type="">Alle</a></li>`;
      const typeLis = types
        .map((t) => `<li><a class="pf-cat-link" href="#" data-type="${t}">${t}</a></li>`)
        .join('');

      categoryList.innerHTML = allLi + typeLis;
      categoryList.hidden = false;

      categoryList.addEventListener('click', (e) => {
        const link = e.target.closest('.pf-cat-link');
        if (!link) return;
        e.preventDefault();
        categoryList.querySelectorAll('.pf-cat-link').forEach((l) => l.classList.remove('pf-cat-link--active'));
        link.classList.add('pf-cat-link--active');
        activeCategory = link.dataset.type || null;
        applyFilters();

        if (sidebarEl && sidebarEl.classList.contains('pf-sidebar--open')) {
          sidebarEl.classList.remove('pf-sidebar--open');
          if (filterToggleBtn) {
            filterToggleBtn.setAttribute('aria-expanded', 'false');
            filterToggleBtn.textContent = '☰ PRODUKTKATEGORIE AUSWÄHLEN';
          }
          const top = root.getBoundingClientRect().top + window.scrollY - 250;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    }

    // ── Filter ─────────────────────────────────────────────────────────────────

    function applyFilters() {
      const term = (filterInput?.value || '').toLowerCase().trim();
      let filtered = allProducts;

      if (activeCategory) {
        filtered = filtered.filter((p) => p.productType === activeCategory);
      }
      if (term) {
        filtered = filtered.filter((p) => p.title.toLowerCase().includes(term));
      }
      renderGrid(filtered);
    }

    if (filterInput) {
      filterInput.addEventListener('input', applyFilters);
    }

    if (filterToggleBtn && sidebarEl) {
      filterToggleBtn.addEventListener('click', () => {
        const isOpen = sidebarEl.classList.toggle('pf-sidebar--open');
        filterToggleBtn.setAttribute('aria-expanded', String(isOpen));
        filterToggleBtn.textContent = isOpen ? 'Filter schließen' : '☰ PRODUKTKATEGORIE AUSWÄHLEN';
      });
    }

    // ── Cart ───────────────────────────────────────────────────────────────────
async function addToCart(variantId, btn) {
  btn.disabled = true;
  btn.textContent = '…';

  try {
    const numericId = variantId.replace(
      'gid://shopify/ProductVariant/',
      ''
    );

    const res = await fetch(
      window.Shopify.routes.root + 'cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(numericId),
              quantity: 1
            }
          ],
          sections: [
            'cart-drawer',
            'cart-icon-bubble'
          ],
          sections_url: window.location.pathname
        })
      }
    );

    const response = await res.json();

    if (!res.ok) {
      let message = response.message;

      showToast(message);

      throw new Error(message);
    }

    // Sections ersetzen
    if (response.sections) {
      Object.entries(response.sections).forEach(([sectionId, html]) => {
        replaceSection(sectionId, html);
      });
    }

    // Cart-Daten holen
    const cartRes = await fetch(
      window.Shopify.routes.root + 'cart.js'
    );

    const cart = await cartRes.json();

    // Bubble setzen / aktualisieren
    updateCartBubble(cart.item_count);

    btn.textContent = 'Hinzugefügt ✓';

    setTimeout(() => {
      btn.textContent = 'In den Warenkorb';
      btn.disabled = false;
    }, 2000);

  } catch (error) {
      if (
        error.message.includes(
          'Die maximale Menge dieses Artikels befindet sich bereits in deinem Warenkorb'
        )
      ) {
        btn.disabled = true;
        btn.textContent = 'Maximum erreicht';
      } else {
        btn.textContent = 'Fehler – nochmal?';
        btn.disabled = false;
      }
  }
}


function findSectionElement(root, sectionId) {
  const escapedId = CSS.escape(sectionId);

  const selectors = [
    `#shopify-section-${escapedId}`,
    `#${escapedId}`,
    sectionId
  ];

  for (const selector of selectors) {
    try {
      const element = root.querySelector(selector);

      if (element) {
        return element;
      }
    } catch (e) {}
  }

  return null;
}


function replaceSection(sectionId, html) {
  if (!html) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const newElement = findSectionElement(doc, sectionId);
  const currentElement = findSectionElement(document, sectionId);

  if (!newElement || !currentElement) {
    console.warn(`Section "${sectionId}" konnte nicht ersetzt werden`);
    return;
  }

  currentElement.replaceWith(newElement);
}


function updateCartBubble(itemCount) {
  let countElement = document.querySelector(
    '.header__cart-count-number'
  );

  if (countElement) {
    countElement.textContent = itemCount;

    const hiddenCount = countElement
      .closest('.header__cart-count')
      ?.querySelector('.visually-hidden');

    if (hiddenCount) {
      hiddenCount.textContent = `${itemCount} Artikel`;
    }

    return;
  }

  const cartIconBubble = document.querySelector(
    '#cart-icon-bubble'
  );

  if (!cartIconBubble) {
    console.warn('#cart-icon-bubble nicht gefunden');
    return;
  }

  cartIconBubble.insertAdjacentHTML(
    'beforeend',
    `
      <div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">
        <span
          aria-hidden="true"
          class="header__cart-count-number"
        >${itemCount}</span>
        <span class="visually-hidden">${itemCount} Artikel</span>
      </div>
    `
  );
}
    

    // ── Init ───────────────────────────────────────────────────────────────────

    async function init() {
      if (!vehicleCode) {
        if (loadingEl) loadingEl.hidden = true;
        if (errorEl) { errorEl.textContent = 'Kein Fahrzeugcode in der URL gefunden.'; errorEl.hidden = false; }
        return;
      }

      try {
        const { products: items, bike } = await fetchResults(vehicleCode);
        renderBikeHeader(bike);

        if (!items.length) {
          if (loadingEl) loadingEl.hidden = true;
          root.innerHTML = '<p class="pf-no-results">Keine passenden Teile für dieses Fahrzeug gefunden.</p>';
          if (countEl) countEl.textContent = '0 Artikel gefunden';
          return;
        }

        allProducts = await fetchProductsByResults(items);
        if (loadingEl) loadingEl.hidden = true;
        renderCategories(allProducts);
        renderGrid(allProducts);
      } catch (err) {
        console.error('Partfinder results error:', err);
        if (loadingEl) loadingEl.hidden = true;
        if (errorEl) { errorEl.textContent = 'Produkte konnten nicht geladen werden. Bitte Seite neu laden.'; errorEl.hidden = false; }
      }
    }

    init();
  })();

function showToast(message) {
  const toast = document.createElement('div');

  toast.className = 'cart-toast';
  toast.textContent = message;

  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('cart-toast--visible');
  });

  setTimeout(() => {
    toast.classList.remove('cart-toast--visible');

    setTimeout(() => {
      toast.remove();
    }, 300);
  }, 4000);
}